package com.merkost.handpass.admin.components

import androidx.compose.runtime.Composable
import androidx.compose.runtime.getValue
import androidx.compose.runtime.mutableStateOf
import androidx.compose.runtime.remember
import androidx.compose.runtime.setValue
import com.merkost.handpass.sharedweb.theme.Danger
import com.merkost.handpass.sharedweb.theme.HandpassSpacing
import com.merkost.handpass.sharedweb.theme.HandpassTypography
import com.merkost.handpass.sharedweb.theme.OnAccent
import com.merkost.handpass.sharedweb.theme.OnSurface
import com.merkost.handpass.sharedweb.theme.OnSurfaceFaint
import com.merkost.handpass.sharedweb.theme.OnSurfaceMuted
import com.merkost.handpass.sharedweb.theme.RadiusControl
import com.varabyte.kobweb.compose.ui.Modifier
import com.varabyte.kobweb.compose.ui.modifiers.backgroundColor
import com.varabyte.kobweb.compose.ui.modifiers.borderRadius
import com.varabyte.kobweb.compose.ui.modifiers.color
import com.varabyte.kobweb.compose.ui.styleModifier
import com.varabyte.kobweb.compose.ui.toAttrs
import org.jetbrains.compose.web.css.DisplayStyle
import org.jetbrains.compose.web.css.JustifyContent
import org.jetbrains.compose.web.css.display
import org.jetbrains.compose.web.css.gap
import org.jetbrains.compose.web.css.justifyContent
import org.jetbrains.compose.web.dom.Div
import org.jetbrains.compose.web.dom.H3
import org.jetbrains.compose.web.dom.P
import org.jetbrains.compose.web.dom.Text

private const val MIN_REASON_LENGTH = 10

data class ConfirmConfig(
    val title: String,
    val message: String,
    val confirmLabel: String = "Confirm",
    val cancelLabel: String = "Cancel",
    val isDestructive: Boolean = false,
    val typeToConfirm: String? = null,
    val callout: String? = null,
    val reasonRequired: Boolean = false,
    val minReasonLength: Int = MIN_REASON_LENGTH,
    val reasonPlaceholder: String = "Reason (recorded in the audit log)…",
)

@Composable
fun AdminConfirmDialog(
    config: ConfirmConfig,
    onConfirm: (reason: String?) -> Unit,
    onDismiss: () -> Unit,
) {
    var typedConfirmation by remember { mutableStateOf("") }
    var reasonText by remember { mutableStateOf("") }

    AdminModal(onDismiss = onDismiss) {
        H3(
            attrs = Modifier
                .color(OnSurface.value())
                .styleModifier {
                    property("font-size", "${HandpassTypography.LG}")
                    property("font-weight", "600")
                    property("margin", "0 0 ${HandpassSpacing.SM} 0")
                }
                .toAttrs()
        ) { Text(config.title) }

        P(
            attrs = Modifier
                .color(OnSurfaceMuted.value())
                .styleModifier {
                    property("font-size", "${HandpassTypography.Body}")
                    property("margin", "0 0 ${HandpassSpacing.Default} 0")
                }
                .toAttrs()
        ) { Text(config.message) }

        if (config.callout != null) {
            Div(
                attrs = Modifier
                    .backgroundColor(Danger.value())
                    .color(OnAccent.value())
                    .borderRadius(RadiusControl.value())
                    .styleModifier {
                        property("padding", "${HandpassSpacing.MD}")
                        property("margin-bottom", "${HandpassSpacing.Default}")
                        property("font-size", "${HandpassTypography.Caption}")
                    }
                    .toAttrs()
            ) { Text(config.callout) }
        }

        if (config.reasonRequired) {
            Div({ style { property("margin-bottom", "${HandpassSpacing.Default}") } }) {
                P(
                    attrs = Modifier
                        .color(OnSurfaceMuted.value())
                        .styleModifier {
                            property("font-size", "${HandpassTypography.Body}")
                            property("margin", "0 0 ${HandpassSpacing.SM} 0")
                        }
                        .toAttrs()
                ) {
                    Text("Reason")
                }
                AdminTextArea(
                    value = reasonText,
                    onChange = { reasonText = it },
                    placeholder = config.reasonPlaceholder,
                )
                P(
                    attrs = Modifier
                        .color(OnSurfaceFaint.value())
                        .styleModifier {
                            property("font-size", "${HandpassTypography.Caption}")
                            property("margin", "${HandpassSpacing.XS} 0 0 0")
                        }
                        .toAttrs()
                ) {
                    Text("${reasonText.trim().length} / ${config.minReasonLength}+ characters")
                }
            }
        }

        if (config.typeToConfirm != null) {
            Div({ style { property("margin-bottom", "${HandpassSpacing.Default}") } }) {
                P(
                    attrs = Modifier
                        .color(OnSurfaceMuted.value())
                        .styleModifier {
                            property("font-size", "${HandpassTypography.Body}")
                            property("margin", "0 0 ${HandpassSpacing.SM} 0")
                        }
                        .toAttrs()
                ) {
                    Text("Type \"${config.typeToConfirm}\" to confirm:")
                }
                AdminInput(
                    value = typedConfirmation,
                    onChange = { typedConfirmation = it },
                    placeholder = config.typeToConfirm,
                )
            }
        }

        Div({
            style {
                display(DisplayStyle.Flex)
                justifyContent(JustifyContent.FlexEnd)
                gap(HandpassSpacing.SM)
            }
        }) {
            AdminButton(
                label = config.cancelLabel,
                onClick = onDismiss,
                variant = AdminButtonVariant.Ghost,
            )
            AdminButton(
                label = config.confirmLabel,
                onClick = {
                    val reason = reasonText.trim().ifBlank { null }
                    if (!config.reasonRequired || (reason != null && reason.length >= config.minReasonLength)) {
                        onConfirm(reason)
                    }
                },
                variant = if (config.isDestructive) AdminButtonVariant.Destructive else AdminButtonVariant.Primary,
                enabled = (config.typeToConfirm == null || typedConfirmation == config.typeToConfirm) &&
                    (!config.reasonRequired || reasonText.trim().length >= config.minReasonLength),
            )
        }
    }
}
